import { CommentatorReaction } from "@/components/commentator-reaction"
import { RevealBlock } from "@/components/reveal-block"
import { SegmentHeader } from "@/components/segment-header"
import { SlamBlock } from "@/components/slam-block"

const TIMING = {
  beatStart: 0.1, // first timeline beat
  beatStagger: 0.15, // between beats
  commentary1: 0.3, // Midge
  commentary2: 0.45, // Grak
  commentary3: 0.6, // Mortifax
} as const

const BEATS = [
  {
    label: "Draft",
    number: "01",
    description: "Pick a race. Name your team. Hire 14 players.",
    highlight: "Five minutes.",
  },
  {
    label: "Play",
    number: "02",
    description:
      "Matches simulate automatically. Watch the replays. Mourn the dead.",
    highlight: null,
  },
  {
    label: "Rise",
    number: "03",
    description: "Players level up. Rivals plot revenge. Season after season.",
    highlight: null,
  },
]

const GOLD = "oklch(0.85 0.28 85)"
const GOLD_DIM = "oklch(0.85 0.28 85 / 0.3)"

interface SeasonArcSectionProps {
  portraitUrls: Record<string, string>
}

export function SeasonArcSection({ portraitUrls }: SeasonArcSectionProps) {
  return (
    <div
      className="relative"
      style={{
        width: "100vw",
        marginLeft: "calc(-50vw + 50%)",
        background:
          "linear-gradient(180deg, oklch(0.07 0.01 55) 0%, oklch(0.12 0.015 55) 15%, oklch(0.12 0.015 55) 85%, oklch(0.07 0.01 55) 100%)",
        borderTop: "1px solid oklch(0.85 0.28 85 / 0.1)",
        borderBottom: "1px solid oklch(0.85 0.28 85 / 0.1)",
      }}
    >
      <div className="mx-auto max-w-4xl px-4 py-16 md:py-24">
        <RevealBlock>
          <SegmentHeader label="How It Works" />
        </RevealBlock>

        {/* Timeline */}
        <div className="relative mt-12">
          {/* Connecting line — visible on md+ */}
          <div
            className="absolute top-7 right-12 left-12 hidden h-px md:block"
            style={{
              background: `linear-gradient(90deg, transparent, ${GOLD_DIM} 15%, ${GOLD_DIM} 85%, transparent)`,
            }}
          />

          <div className="grid gap-8 md:grid-cols-3 md:gap-6">
            {BEATS.map((beat, i) => (
              <RevealBlock
                key={beat.label}
                delay={TIMING.beatStart + i * TIMING.beatStagger}
              >
                <div className="flex flex-col items-center text-center">
                  {/* Step number — big, bold, above everything */}
                  <span
                    className="font-display text-5xl font-bold tracking-tight"
                    style={{
                      color: GOLD,
                      textShadow: `0 0 16px oklch(0.85 0.28 85 / 0.4)`,
                    }}
                  >
                    {beat.number}
                  </span>

                  {/* Label */}
                  <h3
                    className="font-display mt-3 text-xl font-bold tracking-[0.15em] uppercase"
                    style={{
                      color: GOLD,
                      textShadow: `0 0 12px oklch(0.85 0.28 85 / 0.4)`,
                    }}
                  >
                    {beat.label}
                  </h3>

                  {/* Description */}
                  <p
                    className="mt-2 max-w-xs text-sm leading-relaxed"
                    style={{ color: "oklch(0.72 0.02 80)" }}
                  >
                    {beat.description}
                    {beat.highlight && (
                      <>
                        {" "}
                        <span
                          className="font-bold"
                          style={{
                            color: "oklch(0.8 0.3 145)",
                            textShadow: "0 0 8px oklch(0.8 0.3 145 / 0.4)",
                          }}
                        >
                          {beat.highlight}
                        </span>
                      </>
                    )}
                  </p>
                </div>
              </RevealBlock>
            ))}
          </div>
        </div>

        {/* Commentary — bare inside background band, no CommentaryPanel wrapper */}
        <div className="mx-auto mt-16 max-w-2xl space-y-4 md:mt-24">
          <SlamBlock from="left" delay={TIMING.commentary1}>
            <CommentatorReaction
              commentatorKey="midge_tumblebottom"
              name="Midge Tumblebottom"
              mood="excited"
              line="That's it! Draft a team, matches play themselves, and you show up to watch the carnage! EASIEST league you'll ever join!"
              portraitUrl={portraitUrls.midge_tumblebottom_excited ?? null}
              side="left"
            />
          </SlamBlock>
          <SlamBlock from="right" delay={TIMING.commentary2}>
            <CommentatorReaction
              commentatorKey="grak_ironjaw"
              name="Grak Ironjaw"
              mood="neutral"
              line="Your players remember everything. Every block, every injury. They get better — or they don't come back."
              portraitUrl={portraitUrls.grak_ironjaw_neutral ?? null}
              side="right"
            />
          </SlamBlock>
          <SlamBlock from="left" delay={TIMING.commentary3}>
            <CommentatorReaction
              commentatorKey="mortifax_greel"
              name="Mortifax Greel"
              mood="amused"
              line="And when they don't come back... that's when it gets interesting."
              portraitUrl={portraitUrls.mortifax_greel_amused ?? null}
              side="left"
            />
          </SlamBlock>
        </div>
      </div>
    </div>
  )
}
